import { CloseOutlined, InfoCircleFilled, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Checkbox, Radio, Space, UploadFile } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_ASSET_COMMON, DEFAULT_SECTOR } from '../constants/AddAssetDefault';
import { MOUNTING_TYPES_WITH_VALUES } from '../constants/mountingTypes';
import AddAssetProps, {
  AddAssetCommonProps,
  AssetCommonProps,
  AssetDetailsCommonProps,
  AssetImageFaceliftProps,
  SectorCommonProps,
} from '../interfaces/AddAssetProps';
import { ApiHandler } from '../services/apiHandler';
import { exists, generateRandomAlphanumeric, getDeviceTitle, getOrganisationId, getRegion, getRole, getTechType } from '../services/helper';
import './Asset.css';
import ErrorAlert from './ErrorAlert';
import LabelledInputNew from './LabelledInputNew';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import SectorItems from './SectorItems';
import Spinner from './Spinner';
import SuccessAlert from './SuccessAlert';
import WarningAlert from './WarningAlert';
import ConfirmAlert from './confirmAlert';
import AssetImagesModalNew from './AssetImagesModal/AssetImagesModalNew';
import LabelLessInput from './LabelLessInput';
import AzimuthIcon from '../assets/azimuth-leaf.svg';
import CompassIcon from '../assets/compass.svg';

interface AssetProps {
  goBack: any;
  position: { lat: number; lng: number };
  azimuthActivity: any;
  goToProfile: () => void;
  venuesOnMap: any[];
  buildingsOnMap: any[];
  setFloorGeoJSONUrl: (url: any) => void;
  unsetFloorGeoJSON: () => void;
  setVenueId: (venueId: any) => void;
  setBuildingId: (buildingId: any) => void;
  setFloorId: (floorId: any) => void;
  selectedFloor: any;
  setSelectedFloor: (floor: any) => void;
  title: string;
  currentSelectedTechnology: string;
  rmaSerialNumber?: string[];
  assetDetails?: AssetDetailsCommonProps | undefined;
  action: string;
  selectedBuilding: any;
  setSelectedBuilding: (building: any) => void;

  setAssetDropLocation: (location: any) => void;
}

const AddAsset: React.FC<AssetProps> = ({
  goBack,
  position,
  azimuthActivity,
  goToProfile,
  venuesOnMap,
  setVenueId,
  setBuildingId,
  setFloorId,
  setFloorGeoJSONUrl,
  unsetFloorGeoJSON,
  selectedFloor,
  setSelectedFloor,
  title,
  currentSelectedTechnology,
  rmaSerialNumber,
  assetDetails,
  action,
  buildingsOnMap,
  selectedBuilding,
  setSelectedBuilding,
  setAssetDropLocation,
}) => {
  const azimuthInterval = useRef<NodeJS.Timer>();
  let navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({
    message: '',
    alert: false,
  });
  const [warning, setWarning] = useState({
    message: '',
    alert: false,
  });
  const [success, setSuccess] = useState({
    message: '',
    alert: false,
  });
  const [defaultVendorId, setDefaultVendorId] = useState<number>(0);
  const [defaultVendorNameModel, setDefaultVendorNameModel] = useState<string>('');
  const [mountingType, setMountingType] = useState<string>('');
  const [azimuth, setAzimuth] = useState<string[]>(['0']);
  const [azimuthAngle, setAzimuthAngle] = useState<number>(0);
  const [isAzimuthActive, activateAzimuth] = useState<boolean>(false);
  const [assetImages, setAssetImages] = useState<AssetImageFaceliftProps[]>([
    {
      type: 'FACELIFT',
      path: '',
      label: 'Radio Label',
      uploaded: false,
      image: null,
      edited: false,
      latitude: 0,
      longitude: 0,
    },
  ]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [asset, setAsset] = useState<AssetCommonProps>(
    assetDetails?.asset
      ? { ...assetDetails?.asset, serial_number: assetDetails?.sectors.map((sector) => sector.serial_number) }
      : { ...DEFAULT_ASSET_COMMON, serial_number: [] }
  );
  const [vendorNotSelected, showVendorNotSelected] = useState<boolean>(false);
  const [associatedVenue, setAssociatedVenue] = useState<any>(null);
  const [assetOnVenueAlert, setAssetOnVenueAlert] = useState(false);
  const [availableBuildings, setAvailableBuildings] = useState<any[]>([]);
  // const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [availableFloors, setAvailableFloors] = useState<any[]>([]);
  const [directionality, setDirectionality] = useState<'Directional' | 'Omni' | ''>('');
  const [vendorType, setVendorType] = useState<'Airspan' | 'Nokia' | ''>('');
  const [isClicked, setIsClicked] = useState(0);
  // const [options, setOptions] = useState<any[]>([]);
  const [hasCableModem, setHasCableModem] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const [oldSerialNumber, setOldSerialNumber] = useState<string[]>([]);

  const [assetOnBuildingAlert, setAssetOnBuildingAlert] = useState(false);

  // useEffect(() => {
  //   console.log(selectedBuilding, 'Selected Floor changed to:', selectedFloor);
  // }, [selectedFloor, selectedBuilding]);

  useEffect(() => {
    // console.log(action,'RMA Serial Number in Add Asset', rmaSerialNumber);
    if (action === 'rma' && assetDetails?.sectors && assetDetails?.sectors.length > 0) {
      setOldSerialNumber(assetDetails?.sectors?.map((sector) => sector.serial_number));
      setAsset((prevValues: any) => {
        return { ...prevValues, ['serial_number']: rmaSerialNumber };
      });
    }
    return () => {
      stopAzimuthRotation();
      cancelAzimuth();
    };
  }, [assetDetails, action, rmaSerialNumber]);

  useEffect(() => {
    if (action === 'Add') {
      setAsset({ ...DEFAULT_ASSET_COMMON, serial_number: [''] });
      setIsClicked(0);
      setAzimuth(['0']);
    }
  }, [action]);

  useEffect(() => {
    if (action === 'Add') {
      setAsset((prev) => ({
        ...DEFAULT_ASSET_COMMON,
        serial_number: [''],
        tracking_id: prev.tracking_id,
      }));
      setIsClicked(0);
      setAzimuth(['0']);
    }
  }, [title]);

  useEffect(() => {
    if (!assetDetails?.asset?.tracking_id)
      setAsset((prevValues: any) => {
        return { ...prevValues, ['tracking_id']: generateRandomAlphanumeric(6) };
      });
  }, [assetDetails?.asset?.tracking_id]);

  useEffect(() => {
    var defaultVendorType = '';
    if (assetDetails?.asset?.technology_type) {
      if (assetDetails?.asset?.technology_type === 'cbrs_5g') {
        defaultVendorType = assetDetails?.asset?.mounting_type;
        setMountingType(assetDetails?.asset?.mounting_type);
      } else if (assetDetails?.asset?.technology_type === 'wifi_6e') defaultVendorType = 'Wifi';
      else defaultVendorType = assetDetails?.asset?.technology_type;
      getDefaultVendorsInfo(defaultVendorType);
    } else if (currentSelectedTechnology) {
      setMountingType('Aerial');
      getDefaultVendorsInfo(
        currentSelectedTechnology == 'cbrs_5g' ? 'Aerial' : currentSelectedTechnology === 'wifi_6e' ? 'Wifi' : currentSelectedTechnology
      );
    }

    if (assetDetails?.sectors && assetDetails?.sectors.length > 0) {
      setIsClicked(assetDetails?.sectors.length - 1);
      setAzimuth(assetDetails?.sectors.map((sector) => sector.azimuth.toString()));
    }
  }, [assetDetails?.asset, currentSelectedTechnology]);

  useEffect(() => {
    if (exists(mountingType)) {
      if (currentSelectedTechnology === 'cbrs_5g' && mountingType !== '') getDefaultVendorsInfo(mountingType);
    }
  }, [mountingType]);

  useEffect(() => {
    if (selectedBuilding && selectedBuilding?.polygon_points?.length > 0) {
      if (!availableBuildings.map((b) => b.id).includes(selectedBuilding.id)) {
        setSelectedBuilding(null);
        validateIsAssetOnBuilding();
        return;
      }

      let doesContain: boolean = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(position.lat, position.lng),
        new window.google.maps.Polygon({ paths: selectedBuilding.polygon_points })
      );
      if (venuesOnMap?.length > 0) {
        const isExist = venuesOnMap.find((venue) => venue.id === selectedBuilding.venue_id);
        isExist && setAssociatedVenue(isExist);
      }
      if (!doesContain) validateIsAssetOnBuilding();
    } else if (!selectedBuilding) validateIsAssetOnBuilding();
  }, [position, selectedBuilding]);

  useEffect(() => {
    if (action === 'Add') {
      if (!selectedBuilding) setAssetOnVenueAlert(associatedVenue !== null);
    } else {
      setAssetOnVenueAlert(false);
      initiateAssociationWithVenue();
    }
  }, [associatedVenue]);

  useEffect(() => {
    if (selectedBuilding !== null) {
      listFloors();
    }
  }, [selectedBuilding]);

  const onChangeinSectors = (index: number) => {
    const sectorCount = index + 1;
    const angleOfSectors = sectorCount === 1 ? 180 : 360 / sectorCount;
    const baseAzimuth = parseInt(azimuth[0]);
    let newAzimuths: string[] = [];
    for (let i = 0; i < sectorCount; i++) {
      let angle = baseAzimuth + i * angleOfSectors;
      angle = angle % 360;
      if (angle < 0) angle += 360;
      newAzimuths.push(angle.toString());
    }
    setAzimuth(newAzimuths);
  };

  const validateIsAssetOnVenue = () => {
    let contains: boolean = false;
    let tempVenue: any = null;
    for (let venue of venuesOnMap) {
      let doesContain: boolean = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(position.lat, position.lng),
        new window.google.maps.Polygon({ paths: venue?.polygon })
      );
      if (doesContain) {
        contains = doesContain;
        tempVenue = venue;
      }
    }
    if (contains) {
      setAssociatedVenue(tempVenue);
      return true;
    } else {
      setSelectedFloor(null);
      showFloorPlan(null);
      setVenueId('');
      setFloorId('');
      return false;
    }
  };

  const validateIsAssetOnBuilding = () => {
    let contains: boolean = false;
    let tempBuilding: any = null;
    for (let building of buildingsOnMap) {
      let doesContain: boolean = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(position.lat, position.lng),
        new window.google.maps.Polygon({ paths: building?.polygon_points })
      );
      if (doesContain) {
        contains = doesContain;
        tempBuilding = building;
      }
    }
    console.log(buildingsOnMap, 'Venue Contains Asset:', venuesOnMap);

    if (contains) {
      setSelectedBuilding(tempBuilding);
      if (venuesOnMap?.length > 0) {
        const isExist = venuesOnMap.find((venue) => venue.id === tempBuilding.venue_id);
        isExist && setAssociatedVenue(isExist);
        listBuildings();
      }
      setAssetOnBuildingAlert(true);
    } else {
      const ifInVenue = validateIsAssetOnVenue();
      setSelectedBuilding(null);
      if (!ifInVenue) {
        setSelectedFloor(null);
        showFloorPlan(null);
        setVenueId('');
        setAssociatedVenue(null);
      }
      setSelectedBuilding(null);
      setBuildingId('');
      setFloorId('');
      setAvailableBuildings([]);
      setAvailableFloors([]);
    }
  };

  const getDefaultVendorsInfo = (mountingType: string) => {
    setLoading(true);
    const requestParams: any = {
      mountingtype: mountingType,
    };
    ApiHandler({
      apiName: 'getDefaultVendorsInfo',
      body: {},
      params: requestParams,
    })
      .then((response: any) => {
        setLoading(false);
        setDefaultVendor(response.data.vendor_id);
        setDirectionality(response.data.directionality);
        setVendorType(response.data.vendor_type);
        setDefaultVendorNameModel(`${response.data.vendor_name} - ${response.data.vendor_model}`);
        // if (response?.data?.bands?.length > 0) {
        //   var tempBandList: any[] = [];
        //   response?.data?.bands.map((band: any) => {
        //     tempBandList.push({ id: band.id, band_name: band.band_name, band_type: band.band_type });
        //   });
        //   setOptions(tempBandList);
        // }
        if (response.data.has_cable_modem !== undefined) {
          if (response.data.has_cable_modem) setHasCableModem(true);
          else setHasCableModem(false);
        }
      })
      .catch((error: any) => {
        setLoading(false);
        showVendorNotSelected(true);
        if (error.doLogout === true) {
          setError({ message: 'Session Timed Out', alert: true });
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setError({ message: error.data.message, alert: true });
        }
      });
  };

  const setDefaultVendor = (vendorId: any) => {
    if (vendorId === null || vendorId === 0) {
      showVendorNotSelected(true);
      return;
    }
    setDefaultVendorId(vendorId);
  };

  const initiateImageUpload = () => {
    if (asset.tracking_id.trim() === '') {
      setWarning({
        alert: true,
        message: getOrganisationId() !== 1 || vendorType !== 'Nokia' ? 'Asset ID cannot be empty.' : 'UID cannot be empty.',
      });
      return;
    }
    setShowImageModal(true);
  };

  const validateRequest = () => {
    if (asset.height === null || asset.height === undefined) {
      setWarning({ alert: true, message: 'Valid height is required.' });
      return false;
    }
    if (asset.tracking_id.trim() === '') {
      setWarning({
        alert: true,
        message: getOrganisationId() !== 1 || vendorType !== 'Nokia' ? 'Asset ID cannot be empty.' : 'UID cannot be empty.',
      });
      return false;
    } else if (currentSelectedTechnology === 'cbrs_5g' && mountingType === '') {
      setWarning({ alert: true, message: 'Valid Mounting Type is required.' });
      return false;
      //check if any string is empty or has only spaces
    } else if (
      asset.serial_number.length === 0 ||
      asset.serial_number.some((sn) => sn.trim() === '') ||
      !(asset.serial_number.length >= isClicked + 1)
    ) {
      setWarning({ alert: true, message: 'Valid Serial Number is required.' });
      return false;
    } else if (Number(asset.height) < 0 || Number(asset.height) === 0 || isNaN(Number(asset.height))) {
      setWarning({ alert: true, message: 'Valid height is required.' });
      return false;
    } else if (defaultVendorId === 0) {
      showVendorNotSelected(true);
      return false;
    }
    return true;
  };

  const replaceAsset = (requestBody: any) => {
    requestBody['old_serial_number'] = oldSerialNumber;
    ApiHandler({
      apiName: 'replaceAsset',
      body: requestBody,
      params: { tracking_id: assetDetails?.asset.tracking_id },
    })
      .then((response: any) => {
        setLoading(false);
        goBack('home');
      })
      .catch((error: any) => {
        setLoading(false);
        if (error.doLogout === true) {
          setError({ message: 'Session Timed Out', alert: true });
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setError({ message: error.data.message, alert: true });
        }
      });
  };

  const addAsset = () => {
    if (!validateRequest()) return;
    setLoading(true);
    calculateElevation()
      .then((response: any) => {
        let rcamsl = response?.data?.elevation ?? 0 + parseFloat((parseFloat(asset.height.toString() || '0') / 3.281).toFixed(2));
        calculateHaat(rcamsl)
          .then((response: any) => {
            let haat = response?.data?.haat?.toFixed(2) ?? 0;
            let sectorList: SectorCommonProps[] = [];
            if (directionality === 'Directional') {
              azimuth.forEach((value: any, index: any) => {
                const sector: SectorCommonProps = addSectorToRequest(value, index + 1, haat);
                const finalSector: any = { ...sector };
                sectorList.push(finalSector);
              });
            } else if (directionality === 'Omni') {
              const sector: SectorCommonProps = addSectorToRequest('0', 1, haat);
              const finalSector: any = { ...sector };
              sectorList.push(finalSector);
            }

            let uploadedImages: any = [];
            assetImages?.forEach((image: AssetImageFaceliftProps) => {
              if (image.uploaded) {
                uploadedImages.push({ type: image.type, path: image.path, latitude: image.latitude, longitude: image.longitude });
              }
            });

            const { serial_number, ...rest } = asset;
            var apiName = 'addAsset';
            var reqParams = {};

            let requestBody: AddAssetCommonProps = {
              ...rest,
              serial_number: '',
              sectors: [],
            };

            requestBody.serial_number = asset.serial_number[0];
            requestBody.latitude = parseFloat(position.lat.toFixed(6));
            requestBody.longitude = parseFloat(position.lng.toFixed(6));
            requestBody.region = getRegion();
            requestBody.images = uploadedImages;
            requestBody.vendor_id = defaultVendorId;
            requestBody.mounting_type = mountingType;
            requestBody.height = parseFloat(requestBody.height.toString());
            requestBody.sectors = sectorList;
            requestBody.venue_id = associatedVenue ? associatedVenue.id : null;
            requestBody.building_id = selectedBuilding ? selectedBuilding.id : null;
            requestBody.floor_id = selectedFloor ? selectedFloor.id : null;
            requestBody.deployment_location = associatedVenue || selectedBuilding ? 'indoor' : 'outdoor';
            requestBody.technology_type =
              currentSelectedTechnology === 'Wifi' ? 'wifi_6e' : (currentSelectedTechnology as AddAssetProps['technology_type']);
            if (isEmergency) requestBody.job_type = 'emergency';
            requestBody.status = sectorStatus(requestBody.status);

            // if (requestBody.mounting_type === '') requestBody.mounting_type = 'Aerial';

            if (assetDetails?.asset?.tracking_id) {
              apiName = 'updateAsset';
              requestBody.technology_type = assetDetails?.asset?.technology_type as AddAssetProps['technology_type'];
              reqParams = { tracking_id: assetDetails?.asset?.tracking_id };
            }
            // console.log('Request Body before adding values', requestBody);

            if (action === 'rma') {
              replaceAsset(requestBody);
            } else {
              ApiHandler({ apiName: apiName, body: requestBody, params: reqParams })
                .then((response: any) => {
                  setLoading(false);
                  goBack('home');
                  setAzimuth([]);
                })
                .catch((error: any) => {
                  setLoading(false);
                  if (error.doLogout === true) {
                    setError({ message: 'Session Timed Out', alert: true });
                    setTimeout(() => navigate('/login', { replace: true }), 2000);
                  } else {
                    setError({ message: error.data.message, alert: true });
                  }
                });
            }
          })
          .catch((error: any) => {
            setLoading(false);
            setError({
              message: error.response.data.features[0].properties.statusMessage ?? 'HAAT calculation failed.',
              alert: true,
            });
          });
      })
      .catch((error: any) => {
        setLoading(false);
        setError({
          message: error?.response?.data?.features[0]?.properties?.statusMessage ?? 'Elevation Calculation Failed.',
          alert: true,
        });
      });
  };

  const sectorStatus = (status: any) => {
    let userRole: string = getRole()?.toLowerCase() || 'installer';
    if (!['installer', 'engineer'].includes(userRole) && (action === 'rma' || ['RMA Requested', 'RMA in progress'].includes(status)))
      return 'RMA Requested';
    else if (action === 'rma' || ['RMA Requested', 'RMA in progress'].includes(status)) return 'RMA in progress';
    return 'Field Tech';
  };

  const addSectorToRequest = (sectorAzimuth: string, sectorID: number, haat: string): SectorCommonProps => {
    let sector: any = DEFAULT_SECTOR;
    sector.sector_id = sectorID.toString();
    sector.tracking_id = asset.tracking_id;
    sector.height = parseFloat((parseFloat(asset.height.toString() || '0') / 3.281).toFixed(2));
    sector.azimuth = parseFloat(sectorAzimuth);
    sector.haat = haat;
    sector.serial_number = asset.serial_number?.[sectorID - 1];
    sector.status = sectorStatus(assetDetails?.sectors?.[sectorID - 1]?.status);
    return sector;
  };

  const calculateElevation = () => {
    let requestParams = {
      lat: parseFloat(position.lat.toFixed(6)),
      lon: parseFloat(position.lng.toFixed(6)),
      src: 'ned_1',
      unit: 'm',
    };
    // return axios.get('https://geo.fcc.gov/api/contours/elevation.json', {
    //   withCredentials: false,
    //   params: requestParams,
    // });
    return ApiHandler({ apiName: 'fetchElevation', body: requestParams, params: {} });
  };

  const calculateHaat = (rcamsl: number) => {
    let requestParams = {
      lat: parseFloat(position.lat.toFixed(6)),
      lon: parseFloat(position.lng.toFixed(6)),
      nradial: 8,
      rcamsl: rcamsl,
      src: 'ned_1',
      unit: 'm',
    };
    // return axios.get('https://geo.fcc.gov/api/contours/haat.json', {
    //   withCredentials: false,
    //   params: requestParams,
    // });
    return ApiHandler({ apiName: 'fetchHaat', body: requestParams, params: {} });
  };

  const handleInputChange = (e: any) => {
    setAsset((prevValues: any) => {
      return { ...prevValues, [e.target.name]: e.target.value };
    });
  };

  const startIncreaseAzimuth = () => {
    const azimuthProxy = document.getElementsByClassName('azimuth-number') as HTMLCollectionOf<HTMLSpanElement>;
    if (azimuthProxy) {
      Array.from(azimuthProxy).map((element) => {
        element.style.setProperty('transition', 'font-size 0.3s ease-in-out, color 0.3s ease-in-out');
        element.style.setProperty('color', '#0073ff');
        element.style.setProperty('font-size', '12px');
      });
    }
    setAzimuth((prevAzimuth) => prevAzimuth.map((value) => (parseInt(value) + 1 > 360 ? '0' : (parseInt(value) + 1).toString())));
    azimuthInterval.current = setInterval(() => {
      // setAzimuthAngle((azimuthAngle) => (azimuthAngle == 359 ? 0 : azimuthAngle + 1));
      setAzimuth((prevAzimuth) => prevAzimuth.map((value) => (parseInt(value) + 1 > 360 ? '0' : (parseInt(value) + 1).toString())));
    }, 100);
  };

  const startDecreaseAzimuth = () => {
    const azimuthProxy = document.getElementsByClassName('azimuth-number') as HTMLCollectionOf<HTMLSpanElement>;
    if (azimuthProxy) {
      Array.from(azimuthProxy).map((element) => {
        element.style.setProperty('transition', 'font-size 0.3s ease-in-out, color 0.3s ease-in-out');
        element.style.setProperty('color', '#0073ff');
        element.style.setProperty('font-size', '12px');
      });
    }
    setAzimuth((prevAzimuth) => prevAzimuth.map((value) => (parseInt(value) - 1 < 0 ? '359' : (parseInt(value) - 1).toString())));
    azimuthInterval.current = setInterval(() => {
      setAzimuth((prevAzimuth) => prevAzimuth.map((value) => (parseInt(value) - 1 < 0 ? '359' : (parseInt(value) - 1).toString())));
    }, 100);
  };

  const stopAzimuthRotation = () => {
    const azimuthProxy = document.getElementsByClassName('azimuth-number') as HTMLCollectionOf<HTMLSpanElement>;
    if (azimuthProxy) {
      Array.from(azimuthProxy).map((element) => {
        element.style.setProperty('transition', 'font-size 0.3s ease-in-out, color 0.3s ease-in-out');
        element.style.removeProperty('font-size');
        element.style.removeProperty('color');
      });
    }
    clearInterval(azimuthInterval.current);
  };

  const confirmAzimuth = () => {
    var tempAzimuth: string[] = [];
    Array.from({ length: isClicked + 1 }, (_, index) =>
      tempAzimuth.push(
        +azimuth[index] + azimuthAngle > 360 ? Math.abs(+azimuth[index] + azimuthAngle - 360).toString() : (+azimuth[index] + azimuthAngle).toString()
      )
    );
    setAzimuth(tempAzimuth);
    cancelAzimuth();
  };

  const cancelAzimuth = () => {
    azimuthActivity(false);
    activateAzimuth(false);
  };

  const initiateAssociationWithVenue = () => {
    setVenueId(associatedVenue?.id);
    listBuildings();
    setSelectedBuilding(null);
  };
  const initiateAssociationWithBuilding = () => {
    setBuildingId(selectedBuilding?.id);
    listFloors();
    setSelectedFloor(null);
  };

  const listBuildings = () => {
    if (!associatedVenue) return;
    setLoading(true);
    ApiHandler({
      apiName: 'listBuildings',
      body: {},
      params: { venue_id: associatedVenue?.id },
    })
      .then((res) => {
        setLoading(false);
        console.log(res.data, 'response from api to buildings');
        setAvailableBuildings(res.data);
      })
      .catch((error: any) => {
        setLoading(false);
        if (error.doLogout === true) {
          setError({ message: 'Session Timed Out', alert: true });
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setError({ message: error.data.message, alert: true });
        }
      });
  };

  const listFloors = () => {
    // setSelectedFloor(null);
    if (typeof selectedBuilding?.id === 'undefined' || selectedBuilding?.id === null) return;
    setFloorId('');
    setLoading(true);
    ApiHandler({
      apiName: 'listFloors',
      body: {},
      params: { building_id: selectedBuilding?.id },
    })
      .then((res) => {
        setLoading(false);
        console.log(res.data, 'response from api to floors');
        res.data.sort((a: any, b: any) => +a.floor_number - +b.floor_number);
        setAvailableFloors(res.data);
      })
      .catch((error: any) => {
        setLoading(false);
        if (error.doLogout === true) {
          setError({ message: 'Session Timed Out', alert: true });
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setError({ message: error.data.message, alert: true });
        }
      });
  };

  const showFloorPlan = (floorId: any) => {
    unsetFloorGeoJSON();
    if (floorId === null) return;
    setLoading(true);
    ApiHandler({
      apiName: 'getFloorGeoJson',
      body: {},
      params: { floor_id: floorId },
    })
      .then((res) => {
        setLoading(false);
        setFloorGeoJSONUrl(res.data.url);
      })
      .catch((error: any) => {
        setLoading(false);
        if (error.doLogout === true) {
          setError({ message: 'Session Timed Out', alert: true });
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          // setError({ message: error.data.message, alert: true });
        }
      });
  };

  const handleAlertForImages = (alertType: 'error' | 'warning' | 'success', message: string) => {
    if (alertType === 'error') {
      setError({ message: message, alert: true });
    } else if (alertType === 'warning') {
      setWarning({ message: message, alert: true });
    } else if (alertType === 'success') {
      setSuccess({ message: message, alert: true });
    }
  };

  const getTitleName = () => {
    if (currentSelectedTechnology === 'cbrs_5g' || assetDetails?.asset?.technology_type === 'cbrs_5g') {
      return 'CBRS Node Name';
    } else if (currentSelectedTechnology === 'cpe_cbsd' || assetDetails?.asset?.technology_type === 'cpe_cbsd') {
      return 'CPE Name';
    } else if (currentSelectedTechnology === 'tarana' || assetDetails?.asset?.technology_type === 'tarana') {
      return 'Base Node Name';
    } else if (currentSelectedTechnology === 'tarana_cpe' || assetDetails?.asset?.technology_type === 'tarana_cpe') {
      return 'Remote Node Name';
    } else {
      return 'Asset Name';
    }
  };

  const getTechValue = () => {
    if (assetDetails?.asset?.technology_type) {
      return assetDetails?.asset?.technology_type;
    }
    return currentSelectedTechnology;
  };

  const afterUpload = (fileList: any) => {
    setAssetImages(fileList);
    setShowImageModal(false);
  };

  return (
    <>
      <div className="h-100 overflow-auto">
        <div className="app-card position-relative app-card-size right">
          <div className="row-wise vertical-center space-between px-4 py-2">
            <span className="app-card-action-heading fw-bold">
              {`${action == 'Add' ? 'Add new' : 'Edit'} ${
                title?.length > 0
                  ? title
                  : getDeviceTitle[assetDetails?.asset?.technology_type ? assetDetails?.asset?.technology_type : currentSelectedTechnology ?? '']
              }`}
            </span>
            <div
              onClick={() => {
                goBack('home');
              }}>
              <CloseOutlined className="fs-6" />
            </div>
          </div>
          <div className="border-bottom" />

          <div className="overflow-auto">
            <form className="overflow-auto pb-1 pt-2">
              <div className="border-blue px-2 mx-4 py-2 mb-2">
                <Space>
                  <InfoCircleFilled className="info-icon-blue" />
                  <span className="asset-drop-info w-100">{`Click where you'd like to drop the ${
                    getDeviceTitle[assetDetails?.asset?.technology_type ? assetDetails?.asset?.technology_type : currentSelectedTechnology ?? '']
                  }`}</span>
                </Space>
              </div>
              {(currentSelectedTechnology === 'tarana' || assetDetails?.asset?.technology_type === 'tarana') && (
                <div className="px-4 mb-2">
                  <Checkbox className="me-2" value={isEmergency} onChange={() => setIsEmergency((isEmergency) => !isEmergency)} />
                  <label className="fw-bolder">Emergency Restoration</label>
                </div>
              )}

              {associatedVenue || selectedBuilding ? (
                <>
                  <div className="px-4 mb-3 mt-3">
                    {associatedVenue && (
                      <div className="mb-2">
                        <div className="rounded p-2" style={{ backgroundColor: '#e4f1f4' }}>
                          <span className="d-block mb-1 fw-semibold small text-muted">Venue Name</span>
                          <span className="d-block fw-bold text-dark">{associatedVenue.name}</span>
                        </div>
                      </div>
                    )}
                    {/* {selectedBuilding && (
                    <div className="mb-2">
                      <span className="d-block mb-1 fw-bold">Building</span>
                      <span>{selectedBuilding.name}</span>
                    </div>
                  )} */}
                    <div className="mb-2">
                      <span className="d-block mb-1 fw-semibold small text-muted">Building</span>
                      <div className="dropdown">
                        <button disabled={!associatedVenue} className="w-100" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                          {selectedBuilding ? selectedBuilding.name : 'Select'}
                        </button>

                        {availableBuildings.length === 0 && selectedBuilding === null && (
                          <span className="dropdown-item">No buildings available</span>
                        )}
                        <ul className="dropdown-menu w-100">
                          {availableBuildings.length === 0 ? (
                            <span className="dropdown-item">No buildings available</span>
                          ) : (
                            availableBuildings.map((building: any) => {
                              return (
                                <li key={building.id}>
                                  <span
                                    onClick={() => {
                                      setSelectedBuilding(building);
                                      setBuildingId(building.id);
                                      setAssetDropLocation(building?.location);
                                    }}
                                    className="dropdown-item">
                                    {building.name}
                                  </span>
                                </li>
                              );
                            })
                          )}
                        </ul>
                      </div>
                    </div>

                    {selectedBuilding && (
                      <>
                        {/* Floor dropdown */}
                        <div className="mb-2">
                          <span className="d-block mb-1 fw-semibold small text-muted">Floor</span>
                          <div className="dropdown">
                            <button className="w-100" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                              {selectedFloor ? selectedFloor.floor_number : 'Select'}
                            </button>
                            <ul className="dropdown-menu w-100">
                              {availableFloors.length === 0 ? (
                                <span className="dropdown-item">No floors available</span>
                              ) : (
                                availableFloors.map((floor: any) => {
                                  return (
                                    <li key={floor?.id}>
                                      <span
                                        onClick={() => {
                                          setSelectedFloor(floor);
                                          setFloorId(floor?.id);
                                          showFloorPlan(floor?.id);
                                        }}
                                        className="dropdown-item">
                                        {floor?.floor_number}
                                      </span>
                                    </li>
                                  );
                                })
                              )}
                            </ul>
                          </div>
                        </div>
                      </>
                    )}

                    <LabelledInputNew
                      title="Apartment/Suite Number"
                      name="apartment"
                      onChange={(e: any) => {
                        handleInputChange(e);
                      }}
                      value={asset.apartment as string}
                      placeholder=""
                      paddingTitleBottom={'4px'}
                      className="mb-2"
                      titleClassName="fw-semibold small text-muted"
                      textColorRequired={false}
                    />
                    <LabelledInputNew
                      title="Room "
                      name="room_name"
                      onChange={(e: any) => {
                        handleInputChange(e);
                      }}
                      value={asset.room_name as string}
                      placeholder=""
                      paddingTitleBottom={'4px'}
                      titleClassName="fw-semibold small text-muted"
                      textColorRequired={false}
                    />
                  </div>
                </>
              ) : (
                <></>
              )}

              <div className="text-color px-4">
                <div className="border-top pt-3 mb-1"></div>

                {/* <LabelledInputNew
                title={getOrganisationId() !== 1 || vendorType !== 'Nokia' ? 'Asset ID *' : 'UID *'}
                name="tracking_id"
                onChange={(e: any) => {
                  handleInputChange(e);
                }}
                value={asset.tracking_id}
                disabled
                placeholder=""
              /> */}

                <div className="rounded p-2 mb-3" style={{ backgroundColor: '#e4f1f4' }}>
                  <span className="d-block mb-1 fw-semibold small text-muted">{vendorType !== 'Nokia' ? 'Asset ID *' : 'UID *'}</span>
                  <span className="d-block fw-bold text-dark">{asset.tracking_id}</span>
                </div>

                {(currentSelectedTechnology || assetDetails?.asset?.technology_type) === 'cbrs_5g' && (
                  <LabelledInputNew
                    title="gNB ID"
                    name="gnodeb_id"
                    onChange={(e: any) => {
                      handleInputChange(e);
                    }}
                    value={asset.gnodeb_id}
                    placeholder=""
                    paddingTitleBottom={'4px'}
                    titleClassName="fw-semibold small text-muted"
                    textColorRequired={false}
                  />
                )}
                <LabelledInputNew
                  title={getTitleName()}
                  name="cbrs_node_name"
                  onChange={(e: any) => {
                    handleInputChange(e);
                  }}
                  value={asset.cbrs_node_name}
                  placeholder=""
                  paddingTitleBottom={'4px'}
                  titleClassName="fw-semibold small text-muted"
                  textColorRequired={false}
                />
                <LabelledInputNew
                  title="Site ID"
                  name="site_id"
                  onChange={(e: any) => {
                    handleInputChange(e);
                  }}
                  value={asset.site_id}
                  placeholder=""
                  paddingTitleBottom={'4px'}
                  titleClassName="fw-semibold small text-muted"
                  textColorRequired={false}
                />

                <div className="border-top pt-3 mt-3 mb-2">
                  <span className="d-block mb-1 fw-semibold small text-muted">Description</span>
                  <textarea
                    rows={5}
                    id="description"
                    className="textbox-box input-text h-auto w-100 p-2"
                    // placeholder={'Text Area'}
                    name="description"
                    onChange={(e: any) => {
                      handleInputChange(e);
                    }}
                    value={asset.description}></textarea>
                </div>

                <div className="border-top pt-3 mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold small text-muted">Images</span>
                    <span className="badge bg-secondary">{`${
                      assetImages?.filter((image: AssetImageFaceliftProps) => image.uploaded).length
                    }/10 uploaded`}</span>
                  </div>
                  <div
                    className="image-box p-2 col-wise cursor-pointer mb-2"
                    style={{ width: 'auto', display: 'inline-block' }}
                    onClick={() => {
                      initiateImageUpload();
                    }}>
                    <span className="input-label">Upload</span>
                  </div>
                </div>

                {(currentSelectedTechnology || assetDetails?.asset?.technology_type) === 'cbrs_5g' && (
                  <div className="border-top pt-3 mb-3">
                    <div className="mb-2 fw-semibold small text-muted">Mounting Type *</div>
                    <Radio.Group
                      value={mountingType}
                      onChange={(e) => setMountingType(e.target.value)}
                      options={MOUNTING_TYPES_WITH_VALUES}
                      optionType="button"
                    />
                  </div>
                )}

                {/* <LabelledInputNew
                className="mt-4"
                title="Vendor Name - Model"
                name="vendor_name_model"
                disabled
                onChange={(e: any) => {}}
                value={defaultVendorNameModel !== 'undefined - undefined' ? defaultVendorNameModel : ''}
                placeholder=""
              /> */}

                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <div className="rounded p-2" style={{ backgroundColor: '#e4f1f4' }}>
                      <span className="d-block mb-1 fw-semibold small text-muted">Vendor Name - Model *</span>
                      <span className="d-block fw-bold text-dark">{defaultVendorNameModel}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="rounded p-2" style={{ backgroundColor: '#e4f1f4' }}>
                      <span className="d-block mb-1 fw-semibold small text-muted">Directionality *</span>
                      <span className="d-block fw-bold text-dark">{directionality}</span>
                    </div>
                  </div>
                </div>

                {/* <LabelledInputNew title="Directionality" disabled value={directionality} /> */}

                {directionality === 'Directional' && (
                  <div className="border-top pt-3 mb-3">
                    <div className="d-flex mb-1">
                      <span className="d-block mb-2 fw-semibold small text-muted">Select Sectors *</span>
                      {directionality === 'Directional' && (
                        <button
                          className="add-span fw-semibold rounded-2"
                          onClick={(e: any) => {
                            e.preventDefault();
                            azimuthActivity(true);
                            activateAzimuth(true);
                            setAzimuthAngle(0);
                          }}>
                          Set Azimuth Value
                        </button>
                      )}
                    </div>
                    <SectorItems
                      isClicked={isClicked}
                      setIsClicked={(index) => {
                        setIsClicked(index);
                        onChangeinSectors(index);
                      }}
                      disable={['cpe_cbsd', 'cpe_wifi', 'tarana_cpe'].includes(getTechValue()) || action === 'edit'}
                    />
                  </div>
                )}
                {directionality === 'Directional' ? (
                  Array.from({ length: isClicked + 1 }, (_, index) => (
                    <div key={index} className="w-100 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold small text-muted">Serial No - Sector {index + 1} *</span>
                        <span className="fw-semibold small text-muted d-flex align-items-center">
                          <span> Azimuth: </span>
                          <span className="azimuth-number d-flex justify-content-end">{`${azimuth[index] ?? '0'}°`}</span>
                        </span>
                      </div>

                      <LabelLessInput
                        w100
                        className=""
                        name={`serial_number_${index}`}
                        changeVal={(e: any) => {
                          var serial_number_temp = asset.serial_number;
                          serial_number_temp[index] = e.target.value;
                          setAsset((prevValues: any) => {
                            return { ...prevValues, serial_number: serial_number_temp };
                          });
                        }}
                        value={asset?.serial_number[index]}
                        placeholder=""
                        disabled={action === 'edit' && assetDetails?.sectors && assetDetails?.sectors[index]?.status !== 'Field Tech'}
                      />
                    </div>
                  ))
                ) : (
                  <LabelledInputNew
                    title="Serial Number *"
                    // type="number"
                    name="serial_number"
                    className="mb-2"
                    onChange={(e: any) => {
                      setAsset((prevValues: any) => {
                        return { ...prevValues, serial_number: [e.target.value] };
                      });
                    }}
                    value={asset.serial_number[0]}
                    placeholder=""
                    paddingTitleBottom={'4px'}
                    titleClassName="fw-semibold small text-muted"
                    textColorRequired={false}
                  />
                )}

                {/* {directionality === 'Directional' && (
                  <div className="mb-3">
                    <SecondaryButton
                      className="w-100"
                      label="Set Azimuth Value"
                      clicked={() => {
                        azimuthActivity(true);
                        activateAzimuth(true);
                        setAzimuthAngle(0);
                      }}
                      disable={false}
                    />
                  </div>
                )} */}

                <LabelledInputNew
                  title="MAC"
                  name="device_mac"
                  onChange={(e: any) => {
                    handleInputChange(e);
                  }}
                  value={asset.device_mac}
                  placeholder=""
                  paddingTitleBottom={'4px'}
                  textColorRequired={false}
                  titleClassName="fw-semibold small text-muted"
                />

                {/* <div className={`dropdown w-100 mt-2 pb-2 `}>
                <span className="mb-2 d-block">Band *</span>
                <button
                  style={{ background: options.length === 0 ? '#E9ECEF' : '#fff' }}
                  disabled={options.length === 0}
                  className="w-100"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false">
                  {`${
                    asset.band_id !== '' &&
                    asset.band_id !== null &&
                    options?.find((option: any) => option.id === asset.band_id) !== undefined &&
                    options.length > 0
                      ? options?.find((option: any) => option.id === asset.band_id)?.band_type +
                        ' - ' +
                        options?.find((option: any) => option.id === asset.band_id)?.band_name
                      : 'Select Band'
                  }`}
                </button>
                <ul className={`dropdown-menu w-100`}>
                  {options &&
                    options.length > 0 &&
                    options.map((option: any) => {
                      return (
                        <li key={option.id}>
                          <span
                            onClick={() => {
                              setAsset((prevValues: any) => {
                                // console.log('Selected band id', option.id);
                                return { ...prevValues, band_id: option.id };
                              });
                            }}
                            className="dropdown-item">
                            {`${option.band_type} - ${option.band_name}`}
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </div> */}

                {hasCableModem && (
                  <>
                    <LabelledInputNew
                      title="P/N"
                      name="device_pn"
                      onChange={(e: any) => {
                        handleInputChange(e);
                      }}
                      value={asset.device_pn}
                      placeholder=""
                      paddingTitleBottom={'4px'}
                      titleClassName="fw-semibold small text-muted"
                      textColorRequired={false}
                    />
                    <LabelledInputNew
                      title="Docsis Serial #"
                      name="cm_serial_number"
                      onChange={(e: any) => {
                        handleInputChange(e);
                      }}
                      value={asset.cm_serial_number}
                      placeholder=""
                      paddingTitleBottom={'4px'}
                      titleClassName="fw-semibold small text-muted"
                      textColorRequired={false}
                    />
                    <LabelledInputNew
                      title="Docsis Mac"
                      name="cm_mac"
                      onChange={(e: any) => {
                        handleInputChange(e);
                      }}
                      value={asset.cm_mac}
                      placeholder=""
                      paddingTitleBottom={'4px'}
                      titleClassName="fw-semibold small text-muted"
                      textColorRequired={false}
                    />
                    <LabelledInputNew
                      title="Docsis P/N"
                      name="cm_pn"
                      onChange={(e: any) => {
                        handleInputChange(e);
                      }}
                      value={asset.cm_pn}
                      placeholder=""
                      paddingTitleBottom={'4px'}
                      titleClassName="fw-semibold small text-muted"
                      textColorRequired={false}
                    />
                  </>
                )}

                <div className="mt-2 mb-2">
                  <LabelledInputNew
                    title="Antenna Height (ft) *"
                    type="number"
                    name="height"
                    onChange={(e: any) => {
                      handleInputChange(e);
                    }}
                    value={asset.height}
                    placeholder=""
                    paddingTitleBottom={'4px'}
                    titleClassName="fw-semibold small text-muted"
                    textColorRequired={false}
                  />
                  <div className="row g-2 mb-2 mt-2">
                    <div className="col-md-6">
                      <div className="rounded p-2" style={{ backgroundColor: '#e4f1f4' }}>
                        <span className="d-block mb-1 fw-semibold small text-muted">Latitude *</span>
                        <span className="d-block fw-bold text-dark">{position.lat.toFixed(6)}</span>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="rounded p-2" style={{ backgroundColor: '#e4f1f4' }}>
                        <span className="d-block mb-1 fw-semibold small text-muted">Longitude *</span>
                        <span className="d-block fw-bold text-dark">{position.lng.toFixed(6)}</span>
                      </div>
                    </div>
                  </div>
                  {/* <div className="row gx-2 mb-2">
                    <div className="col-6">
                      <LabelledInputNew
                        title="Latitude *"
                        disabled
                        value={position.lat.toFixed(6)}
                        paddingTitleBottom={'4px'}
                        textColorRequired={false}
                        titleClassName="fw-semibold small text-muted"
                      />
                    </div>
                    <div className="col-6">
                      <LabelledInputNew
                        title="Longitude *"
                        disabled
                        value={position.lng.toFixed(6)}
                        paddingTitleBottom={'4px'}
                        textColorRequired={false}
                        titleClassName="fw-semibold small text-muted"
                      />
                    </div> 
                  </div> */}
                </div>
              </div>
            </form>
          </div>
          <div className="row-wise vertical-center px-4 py-2 gap-2">
            <SecondaryButton className="w-100" label={'Cancel'} clicked={() => goBack('home')} disable={false} />
            <PrimaryButton className="w-100 blue-bg" label="Save" clicked={addAsset} />
          </div>
        </div>

        {/* 
      {isAzimuthActive && (
        <>
          <CloseOutlined title="Close azimuth" className="azimuth-icon-position-reset" onClick={cancelAzimuth} />
          {/* <CheckOutlined className="azimuth-icon-position-confirm" onClick={confirmAzimuth} /> 
        </>
      )} 
      */}

        <Spinner show={loading} />

        <ConfirmAlert
          message={`Vendor for ${mountingType} is not selected. Set it in your profile.`}
          primaryText={'Take me there'}
          secondaryText={"I'll do it later"}
          secondaryAction={() => {
            showVendorNotSelected(false);
          }}
          visible={vendorNotSelected}
          title={`Vendor not selected `}
          primaryAction={() => {
            goToProfile();
          }}
          onClose={() => {
            showVendorNotSelected(false);
          }}
        />

        <ConfirmAlert
          message={`You have moved the location pin in the ${associatedVenue?.name} venue.`}
          note="Asset will be associated to this venue."
          primaryText={'OK'}
          secondaryText={null}
          secondaryAction={() => {
            setAssetOnVenueAlert(false);
            setAssociatedVenue(null);
          }}
          visible={assetOnVenueAlert}
          title={`Location landed in venue `}
          primaryAction={() => {
            setAssetOnVenueAlert(false);
            initiateAssociationWithVenue();
          }}
          onClose={() => {
            setAssetOnVenueAlert(false);
            setAssociatedVenue(null);
          }}
        />

        <ConfirmAlert
          message={`You have moved the location pin in the ${selectedBuilding?.name} building.`}
          note="Asset will be associated to this building."
          primaryText={'OK'}
          secondaryText={null}
          secondaryAction={() => {
            setAssetOnBuildingAlert(false);
            setSelectedBuilding(null);
          }}
          visible={assetOnBuildingAlert}
          title={`Location landed in building `}
          primaryAction={() => {
            setAssetOnBuildingAlert(false);
            // initiateAssociationWithBuilding();
          }}
          onClose={() => {
            setAssetOnBuildingAlert(false);
            // setSelectedBuilding(null);
          }}
        />

        <AssetImagesModalNew
          show={showImageModal}
          close={() => {
            setShowImageModal(false);
          }}
          alert={handleAlertForImages}
          trackingId={asset.tracking_id}
          afterUpload={afterUpload}
          fileList={fileList}
          setFileList={setFileList}
        />

        <ErrorAlert
          show={error.alert}
          onDismiss={() => {
            setError({ message: '', alert: false });
          }}
          message={error.message}
        />
        <SuccessAlert
          show={success.alert}
          onDismiss={() => {
            setSuccess({ message: '', alert: false });
          }}
          message={success.message}
        />
        <WarningAlert
          show={warning.alert}
          onDismiss={() => {
            setWarning({ message: '', alert: false });
          }}
          message={warning.message}
        />
      </div>

      {/* Azimuth Image - rotates on button click */}
      <div className={'position-absolute azimuth-icon-position ' + (isAzimuthActive ? '' : 'd-none')}>
        {Array.from({ length: isClicked + 1 }, (_, index) => (
          <img
            src={AzimuthIcon}
            style={{
              transform: 'rotate(' + azimuth[index] + 'deg)',
              transformOrigin: 'bottom',
              position: 'absolute',
              left: 'calc(46% - 9px)',
              top: '-7px',
            }}
          />
        ))}
        <img src={CompassIcon} />
        <div className="azimuth-icon-position-controls">
          <CloseOutlined title="Close azimuth" className="azimuth-icon-position-reset" onClick={cancelAzimuth} />
          <MinusOutlined
            onMouseDown={startDecreaseAzimuth}
            onMouseUp={stopAzimuthRotation}
            onMouseLeave={stopAzimuthRotation}
            style={{ fontSize: 24, backgroundColor: '#DADDE2', borderRadius: '8px 0 0 8px', margin: 0, padding: '8px', cursor: 'pointer' }}
          />
          <span style={{ margin: '0 10px', color: '#1677FF', padding: '8px', fontSize: '20px' }}>{azimuth[0]}°</span>
          <PlusOutlined
            onMouseDown={startIncreaseAzimuth}
            onMouseUp={stopAzimuthRotation}
            onMouseLeave={stopAzimuthRotation}
            style={{ fontSize: 24, backgroundColor: '#DADDE2', borderRadius: '0 8px 8px 0', margin: 0, padding: '8px', cursor: 'pointer' }}
          />
        </div>
      </div>
    </>
  );
};

export default AddAsset;
